import React from 'react';
import { Shield, ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0a1628] text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <a 
            href="/"
            className="inline-flex items-center gap-2 text-[#d4af37] hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10 text-[#d4af37]" />
            <div>
              <h1 className="text-3xl font-bold">Privacy Policy</h1>
              <p className="text-gray-400">Last Updated: {currentDate}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to ZamZam Umrah ("we," "our," or "us"). We are committed to protecting your personal information 
              and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard 
              your information when you visit our website <strong>zamzamumrah.co.uk</strong>, use our services, 
              or interact with us through social media platforms including Facebook, Instagram, WhatsApp, Google, 
              and other third-party services.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By using our services, you agree to the collection and use of information in accordance with this policy. 
              If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-[#0a1628] mt-6 mb-3">2.1 Personal Information You Provide</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              We collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Fill out enquiry forms on our website</li>
              <li>Contact us via phone, email, or WhatsApp</li>
              <li>Book an Umrah package</li>
              <li>Subscribe to our newsletter</li>
              <li>Interact with us on social media platforms</li>
              <li>Participate in surveys or promotions</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              This information may include:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Full name (as per passport)</li>
              <li>Email address</li>
              <li>Phone number (including WhatsApp number)</li>
              <li>Passport details</li>
              <li>Date of birth</li>
              <li>Nationality</li>
              <li>Travel preferences and requirements</li>
              <li>Payment information</li>
              <li>Emergency contact details</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0a1628] mt-6 mb-3">2.2 Information Collected Automatically</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              When you visit our website, we automatically collect certain information, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>IP address and location data</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Pages visited and time spent</li>
              <li>Referring website addresses</li>
              <li>Click patterns and user behavior</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0a1628] mt-6 mb-3">2.3 Information from Social Media Platforms</h3>
            <p className="text-gray-700 leading-relaxed">
              When you interact with us through social media platforms (Facebook, Instagram, WhatsApp, etc.), 
              we may receive information about you including your name, profile picture, contact information, 
              and any messages you send us. This information is subject to the privacy policies of those platforms.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Service Delivery:</strong> To process your Umrah bookings, visa applications, and travel arrangements</li>
              <li><strong>Communication:</strong> To respond to your enquiries and provide customer support via email, phone, or WhatsApp</li>
              <li><strong>Marketing:</strong> To send you promotional materials, newsletters, and special offers (with your consent)</li>
              <li><strong>Personalization:</strong> To customize your experience and provide tailored recommendations</li>
              <li><strong>Analytics:</strong> To analyze website usage and improve our services</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
              <li><strong>Security:</strong> To detect, prevent, and address fraud, security issues, or technical problems</li>
            </ul>
          </section>

          {/* Cookies and Tracking Technologies */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">4. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to collect and track information about your 
              browsing activities. These technologies help us:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our website</li>
              <li>Deliver relevant advertisements</li>
              <li>Measure the effectiveness of our marketing campaigns</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0a1628] mt-6 mb-3">4.1 Types of Cookies We Use</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
              <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
              <li><strong>Social Media Cookies:</strong> Enable social media features and tracking</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0a1628] mt-6 mb-3">4.2 Meta (Facebook) Pixel</h3>
            <p className="text-gray-700 leading-relaxed">
              We use the Meta (Facebook) Pixel to measure the effectiveness of our advertising, understand 
              the actions people take on our website, and deliver targeted advertisements on Facebook and 
              Instagram. This pixel may collect information about your device, browsing activity, and 
              interactions with our website. You can opt out of Facebook's data collection by adjusting 
              your Facebook Ad Settings.
            </p>

            <h3 className="text-xl font-semibold text-[#0a1628] mt-6 mb-3">4.3 Google Analytics</h3>
            <p className="text-gray-700 leading-relaxed">
              We use Google Analytics to analyze website traffic and user behavior. Google Analytics uses 
              cookies to collect anonymous information about how visitors use our website. You can opt out 
              by installing the Google Analytics Opt-out Browser Add-on.
            </p>
          </section>

          {/* Social Media Integration */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">5. Social Media Integration</h2>
            
            <h3 className="text-xl font-semibold text-[#0a1628] mt-6 mb-3">5.1 Facebook and Instagram</h3>
            <p className="text-gray-700 leading-relaxed">
              We maintain business pages on Facebook and Instagram. When you interact with our pages, 
              like or share our content, or send us messages, your information is subject to Meta's 
              Privacy Policy. We may use Facebook's tools to create custom audiences for advertising purposes.
            </p>

            <h3 className="text-xl font-semibold text-[#0a1628] mt-6 mb-3">5.2 WhatsApp Business</h3>
            <p className="text-gray-700 leading-relaxed">
              We use WhatsApp Business to communicate with customers. When you contact us via WhatsApp, 
              we collect your phone number, name, and message content. This information is used to respond 
              to your enquiries and may be stored in our CRM system for follow-up purposes. WhatsApp's 
              privacy policy applies to all WhatsApp communications.
            </p>

            <h3 className="text-xl font-semibold text-[#0a1628] mt-6 mb-3">5.3 Google Services</h3>
            <p className="text-gray-700 leading-relaxed">
              We use various Google services including Google Analytics, Google Ads, and Google Maps. 
              When you interact with these services on our website, your information may be collected 
              by Google in accordance with their Privacy Policy.
            </p>

            <h3 className="text-xl font-semibold text-[#0a1628] mt-6 mb-3">5.4 TikTok and Other Platforms</h3>
            <p className="text-gray-700 leading-relaxed">
              We may maintain presence on other social media platforms including TikTok, Twitter/X, 
              YouTube, and LinkedIn. Interactions on these platforms are subject to their respective 
              privacy policies.
            </p>
          </section>

          {/* Data Sharing and Disclosure */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">6. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may share your personal information in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> With airlines, hotels, visa processing agencies, and transport providers to fulfill your booking</li>
              <li><strong>Government Authorities:</strong> With Saudi Arabian authorities for visa processing and Umrah permits</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of company assets</li>
              <li><strong>With Your Consent:</strong> For any other purpose with your explicit consent</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">7. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes for which 
              it was collected, including to satisfy legal, accounting, or reporting requirements. Typically, 
              we retain booking and travel records for 7 years, marketing preferences until you opt out, and 
              enquiry data for 3 years unless you request deletion.
            </p>
          </section>

          {/* Your Rights (GDPR) */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">8. Your Rights (GDPR Compliance)</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Under the General Data Protection Regulation (GDPR) and UK Data Protection laws, you have the following rights:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
              <li><strong>Right to Data Portability:</strong> Request transfer of your data to another organization</li>
              <li><strong>Right to Object:</strong> Object to processing of your data for direct marketing</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise any of these rights, please contact us using the details provided below. 
              We will respond to your request within 30 days.
            </p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">9. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Your information may be transferred to and processed in countries outside the UK and European 
              Economic Area (EEA), including Saudi Arabia for visa processing and travel arrangements. 
              We ensure appropriate safeguards are in place to protect your data during such transfers, 
              including standard contractual clauses approved by the relevant authorities.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">10. Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information 
              against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>SSL/TLS encryption for data in transit</li>
              <li>Secure data storage with access controls</li>
              <li>Regular security assessments and updates</li>
              <li>Staff training on data protection</li>
              <li>Incident response procedures</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure, 
              and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">11. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are not directed to children under 16. We do not knowingly collect personal 
              information from children without parental consent. If you believe we have collected 
              information from a child without appropriate consent, please contact us immediately.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">12. Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website may contain links to third-party websites, including airline booking systems, 
              payment processors, and social media platforms. We are not responsible for the privacy 
              practices of these external sites. We encourage you to review their privacy policies 
              before providing any personal information.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">13. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices 
              or legal requirements. We will notify you of any material changes by posting the updated 
              policy on our website with a new "Last Updated" date. We encourage you to review this 
              policy periodically.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-[#0a1628] mb-4">14. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our 
              data practices, please contact us:
            </p>
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#d4af37] rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Email</div>
                  <a href="mailto:info@zamzamumrah.co.uk" className="text-[#0a1628] font-medium hover:text-[#d4af37]">
                    info@zamzamumrah.co.uk
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#d4af37] rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Phone</div>
                  <div className="text-[#0a1628] font-medium">+44 1582 800 304 / +44 1908 220 000</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#d4af37] rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Address</div>
                  <div className="text-[#0a1628] font-medium">ZamZam Umrah, United Kingdom</div>
                </div>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mt-4">
              You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) 
              if you believe your data protection rights have been violated:
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Information Commissioner's Office</strong><br />
              Website: <a href="https://ico.org.uk" className="text-[#d4af37] hover:underline" target="_blank" rel="noopener noreferrer">ico.org.uk</a><br />
              Phone: 0303 123 1113
            </p>
          </section>

          {/* Consent */}
          <section className="bg-[#0a1628] text-white rounded-xl p-6 mt-8">
            <h2 className="text-xl font-bold mb-3">Your Consent</h2>
            <p className="text-gray-300 leading-relaxed">
              By using our website and services, you consent to the collection, use, and sharing of your 
              information as described in this Privacy Policy. If you do not agree with this policy, 
              please discontinue use of our services.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} ZamZam Umrah. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <a href="/terms" className="hover:text-[#d4af37]">Terms & Conditions</a>
            <span>|</span>
            <a href="/" className="hover:text-[#d4af37]">Home</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
